import { canAccessApp, clientDebugLog, isClientUser } from '@apexx/auth';
import {
	accessTokenLogFingerprint,
	canSwitchHubApps,
	getAccessibleHubAppIds,
	type AppHandle,
	type AppModule,
	type AppRegistryEntry,
	type ShellContext,
} from '@apexx/shell-contract';
import { normalizeHubTheme } from './theme';

export const APP_REGISTRY: AppRegistryEntry[] = [
	{
		id: 'machines',
		name: 'Customer Support',
		description: 'Manage machines, services, and field operations',
		icon: 'precision_manufacturing',
		requiredRole: 'app:machines',
		load: () => import('@apexx/machines/mount').then((m) => m.default),
	},
	{
		id: 'quality',
		name: 'Quality',
		description: 'Incoming inspections and assembly quality review',
		icon: 'checklist',
		requiredRole: 'app:quality',
		load: () => import('@apexx/quality/mount').then((m) => m.default),
	},
	{
		id: 'chat',
		name: 'Team Chat',
		description: 'Team messaging and conversations',
		icon: 'comments',
		requiredRole: 'app:chat',
		load: () => import('@apexx/chat/mount').then((m) => m.default),
	},
	{
		id: 'documents',
		name: 'Documents',
		description: 'Browse and manage documents and folders',
		icon: 'folder',
		requiredRole: 'app:documents',
		load: () => import('@apexx/documents/mount').then((m) => m.default),
	},
	{
		id: 'tasks',
		name: 'Tasks',
		description: 'Track and manage work tasks',
		icon: 'todo',
		requiredRole: 'app:tasks',
		load: () => import('@apexx/tasks/mount').then((m) => m.default),
	},
	{
		id: 'training',
		name: 'Training',
		description: 'Courses, learning paths, and certifications',
		icon: 'cap',
		requiredRole: 'app:training',
		load: () => import('@apexx/training/mount').then((m) => m.default),
	},
];

export function getAccessibleApps(accessToken?: string): AppRegistryEntry[] {
	const accessibleIds = new Set<string>(getAccessibleHubAppIds(accessToken));

	return APP_REGISTRY.filter((app) => accessibleIds.has(app.id));
}

const MACHINES_APP_ID = 'machines';

/**
 * When set, single-app clients with MM access skip the launcher and open this app.
 * Multi-app clients (and all staff) reach the launcher normally.
 */
export function getClientAutoLaunchAppId(accessToken: string | undefined): string | undefined {
	if (!isClientUser(accessToken) || canSwitchHubApps(accessToken)) {
		return undefined;
	}

	const machinesApp = getAppRegistryEntry(MACHINES_APP_ID);
	if (!machinesApp || !canAccessApp(accessToken, machinesApp.requiredRole)) {
		return undefined;
	}

	return MACHINES_APP_ID;
}

export function getAppRegistryEntry(appId: string): AppRegistryEntry | undefined {
	return APP_REGISTRY.find((app) => app.id === appId);
}

/** Outcome of {@link ShellOrchestrator.launchApp} — callers sync sub-paths only after a fresh mount. */
export type LaunchAppOutcome = 'mounted' | 'propagated';

export class ShellOrchestrator {
	private activeHandle: AppHandle | null = null;
	private activeModule: AppModule | null = null;
	private activeContainer: HTMLElement | null = null;
	private activeAppId: string | null = null;
	private lastPropagatedToken: string | null = null;

	/** True when this app is already mounted in the given container (token refresh / re-launch). */
	isActiveInContainer(appId: string, container: HTMLElement): boolean {
		return Boolean(
			this.activeHandle
			&& this.activeAppId === appId
			&& this.activeContainer === container,
		);
	}

	async launchApp(appId: string, container: HTMLElement, ctx: ShellContext): Promise<LaunchAppOutcome> {
		// Same app already mounted in this container — skip remount. Callers may re-fire
		// when async shell fields (e.g. appVersion) resolve; tearing down mid-patch crashes Vue.
		// Still push refreshed tokens so embedded API clients do not keep a stale bearer.
		if (this.isActiveInContainer(appId, container)) {
			await this.propagateAccessToken(ctx.accessToken);
			return 'propagated';
		}

		await this.unmountActive();

		const entry = getAppRegistryEntry(appId);
		if (!entry) {
			throw new Error(`Unknown app: ${appId}`);
		}

		if (!canAccessApp(ctx.accessToken, entry.requiredRole)) {
			throw new Error('You do not have access to this app');
		}

		if (entry.disabled) {
			throw new Error('This app is not yet available');
		}

		const module = await entry.load();
		this.activeModule = module;
		this.activeAppId = appId;
		this.activeContainer = container;
		this.activeHandle = await module.mount(container, ctx);
		this.lastPropagatedToken = ctx.accessToken ?? null;

		if (ctx.accessToken) {
			clientDebugLog('info', '[hub:token] mounted app with access token', {
				appId,
				token: accessTokenLogFingerprint(ctx.accessToken),
			});
		}

		return 'mounted';
	}

	/**
	 * Forwards a refreshed hub access token into the active sub-app without remounting.
	 * No-op when nothing is mounted, the token is unchanged, or the handle lacks updateAccessToken.
	 */
	async propagateAccessToken(token: string | undefined): Promise<void> {
		if (!token || !this.activeHandle || !this.activeAppId) {
			return;
		}

		if (token === this.lastPropagatedToken) {
			return;
		}

		const update = this.activeHandle.updateAccessToken;
		if (!update) {
			clientDebugLog('warn', '[hub:token] active app has no updateAccessToken; token not propagated', {
				appId: this.activeAppId,
				token: accessTokenLogFingerprint(token),
			});
			return;
		}

		await update(token);
		this.lastPropagatedToken = token;
		clientDebugLog('info', '[hub:token] propagated refreshed access token', {
			appId: this.activeAppId,
			token: accessTokenLogFingerprint(token),
		});
	}

	async unmountActive(): Promise<void> {
		if (this.activeHandle) {
			await this.activeHandle.unmount();
			this.activeHandle = null;
			this.activeModule = null;
			this.activeAppId = null;
			this.activeContainer = null;
			this.lastPropagatedToken = null;
		}

		normalizeHubTheme();
	}

	getActiveAppId(): string | null {
		return this.activeModule?.id ?? this.activeAppId;
	}

	/**
	 * Push a hub sub-path into the currently mounted app's router (no remount).
	 * No-op when nothing is mounted, the app id does not match, or the handle lacks syncPath.
	 */
	async syncActiveSubPath(appId: string, subPath: string): Promise<void> {
		if (!this.activeHandle || this.activeAppId !== appId) {
			return;
		}

		const syncPath = this.activeHandle.syncPath;
		if (!syncPath) {
			return;
		}

		await syncPath(subPath);
	}
}

export const shellOrchestrator = new ShellOrchestrator();
